import type { VideoEmbedComponent } from "../../types";
import { toEmbedUrl } from "../../utils/videoEmbed";
import { PageTitle } from "./PageTitle";

/**
 * Responsive 16:9 video embed block.
 *
 * Renders the `embedUrl` of a `videos` content-type record (or any authored
 * page) as a framed player. The stored `embedUrl` is a free-form value — a
 * YouTube watch/share link, a Vimeo page link, or an already-embeddable URL —
 * so it runs through `toEmbedUrl`, which normalises watch/page URLs into a
 * proper `<iframe src>` (`youtube.com/watch?v=ID` and `youtu.be/ID` ->
 * `youtube.com/embed/ID`; `vimeo.com/ID` -> `player.vimeo.com/video/ID`) and
 * passes generic iframe-embeddable URLs through unchanged.
 *
 * Unlike `DataRecordPdfPreview`, `embedUrl` is a full URL (not a
 * bucket-relative storage key), so this stays a server component — there is no
 * `assetResolver` to call. It self-hides — mirroring the `isUnresolved` guard
 * in `DataRecordDetailRenderer` — when `embedUrl` is empty or still an
 * unsubstituted `{token}` placeholder.
 *
 * Colours follow the site system exactly — surfaces `#F4F8FC`, `slate-200`
 * border, and the optional `title` reuses the canonical `PageTitle`
 * (primary-blue `#005CA9` Aleo `<h1>` with a yellow `#FFA401` underline), the
 * same component `DataRecordDetailRenderer` renders its title through.
 */
export function VideoEmbedRenderer({ component }: { component: VideoEmbedComponent }) {
  const { title, embedUrl } = component.content;

  // No resolvable URL (empty value or unresolved {token}) -> render nothing.
  const src = toEmbedUrl(embedUrl);
  if (!src) return null;

  const titleText = (title ?? "").trim();

  return (
    <section>
      {/* Optional canonical site title above the embed. */}
      {titleText ? <PageTitle title={titleText} /> : null}

      {/* Responsive 16:9 container — the absolutely-positioned iframe fills the
          padding-top box so the player keeps its aspect ratio at any width. */}
      <div className="relative w-full overflow-hidden rounded border border-slate-200 bg-[#F4F8FC] pt-[56.25%]">
        <iframe
          title={titleText || "Vidéo"}
          src={src}
          className="absolute inset-0 h-full w-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
        />
      </div>
    </section>
  );
}
